import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FaTimes } from "react-icons/fa";
import LanguageSwitcher from "./LanguageSwitcher";

const NavLink = ({ to, children, onClick }) => {
    const location = useLocation();
    const active = location.pathname === to;
    return (
        <Link
            to={to}
            onClick={onClick}
            className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${active
                ? "bg-gradient-to-br from-vsred to-vsred-dark text-white shadow-red-glow"
                : "text-gray-300 hover:text-white hover:bg-white/5"
                }`}
        >
            {children}
            {active && (
                <motion.div
                    layoutId="activeNav"
                    className="absolute inset-0 bg-gradient-to-br from-vsred to-vsred-dark rounded-lg -z-10"
                    initial={false}
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
            )}
        </Link>
    );
};

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Lock body scroll when mobile menu is open
    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        // Cleanup on unmount
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [mobileMenuOpen]);

    useEffect(() => {
        if (location.pathname === '/' && performance.getEntriesByType('navigation')[0]?.type === 'navigate') {
            // Smooth scroll to top when navigated to home
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [location]);

    return (
        <motion.header
            className={`fixed w-full z-50 header-border-fade transition-all duration-300 overflow-hidden
    ${mobileMenuOpen
                    ? "bg-vsblack border-b border-gray-800 shadow-dark"
                    : scrolled
                        ? "bg-vsblack/95 border-b border-gray-800 shadow-dark backdrop-blur-md"
                        : "bg-gradient-to-b from-black/70 to-transparent border-b border-transparent"
                }`}
        >
            <div
                className={`max-w-7xl mx-auto px-6 transition-all duration-300 ${scrolled ? "py-2" : "py-4"
                    }`}
            >
                <div className="flex items-center justify-between">
                    <motion.div
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="flex items-center"
                    >
                        <Link
                            to="/"
                            className="flex items-center gap-3 group"
                            onClick={(e) => {
                                if (window.location.pathname === '/') {
                                    e.preventDefault();
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                }
                            }}
                        >
                            <img
                                src="/assets/images/logo.png"
                                alt="VSGrill Logo"
                                className={`transition-all duration-300 object-contain ${scrolled ? "h-10" : "h-12"
                                    }`}
                            />
                            <div className="hidden sm:block">
                                <div className="text-white font-bold text-xl font-serif tracking-tight group-hover:text-vsred-light transition-colors">
                                    VSGrill
                                </div>
                                <div className="text-xs text-gray-400 -mt-0.5 tracking-wide">{t('header.tagline')}</div>
                            </div>
                        </Link>
                    </motion.div>

                    {/* Desktop Navigation */}
                    <motion.nav
                        className="hidden md:flex items-center gap-2"
                        initial={{ x: 20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        <NavLink to="/">{t('nav.home')}</NavLink>
                        <NavLink to="/products">{t('nav.products')}</NavLink>
                        <NavLink to="/gallery">{t('nav.gallery')}</NavLink>
                        <NavLink to="/about">{t('nav.about')}</NavLink>
                        <button
                            className="ml-3 btn-primary"
                            onClick={(e) => {
                                e.preventDefault();
                                const quoteSection = document.getElementById('quote');
                                if (quoteSection) {
                                    // Already on home page, just scroll to quote section
                                    quoteSection.scrollIntoView({ behavior: 'smooth' });
                                } else {
                                    // Navigate to home page and scroll to quote section
                                    navigate('/');
                                    // Use setTimeout to ensure navigation completes before scrolling
                                    setTimeout(() => {
                                        const quoteElement = document.getElementById('quote');
                                        if (quoteElement) {
                                            quoteElement.scrollIntoView({ behavior: 'smooth' });
                                        }
                                    }, 100);
                                }
                            }}
                        >
                            {t('nav.requestQuote')}
                        </button>
                        <LanguageSwitcher />
                    </motion.nav>

                    {/* Mobile Menu Button - Inside Header Container */}
                    <motion.button
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        className="md:hidden flex flex-col justify-center items-center w-10 h-10 rounded-lg border border-gray-800 bg-vsblack text-white shadow-dark hover:text-vsred-light transition-all"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.2 }}
                    >
                        <motion.span
                            className="block w-6 h-[2px] bg-white rounded-full"
                            animate={{
                                rotate: mobileMenuOpen ? 45 : 0,
                                y: mobileMenuOpen ? 6 : 0,
                            }}
                            transition={{ duration: 0.3 }}
                        />
                        <motion.span
                            className="block w-6 h-[2px] bg-white rounded-full my-1"
                            animate={{
                                opacity: mobileMenuOpen ? 0 : 1,
                            }}
                            transition={{ duration: 0.2 }}
                        />
                        <motion.span
                            className="block w-6 h-[2px] bg-white rounded-full"
                            animate={{
                                rotate: mobileMenuOpen ? -45 : 0,
                                y: mobileMenuOpen ? -6 : 0,
                            }}
                            transition={{ duration: 0.3 }}
                        />
                    </motion.button>
                </div>


                {/* Mobile Menu Overlay */}
                <AnimatePresence>
                    {mobileMenuOpen && (
                        <>
                            {/* Backdrop */}
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.25, ease: "easeInOut" }}
                                className="fixed inset-0 bg-black/60 backdrop-blur-md z-40 md:hidden"
                                onClick={() => setMobileMenuOpen(false)}
                            />

                            {/* Mobile Drawer */}
                            <motion.div
                                initial={{ x: "100%", opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                exit={{ x: "100%", opacity: 0 }}
                                transition={{ type: "spring", stiffness: 120, damping: 15 }}
                                className="fixed top-0 right-0 h-full w-80 sm:w-96 bg-vsblack border-l border-gray-800/80 z-[60] md:hidden flex flex-col rounded-l-3xl shadow-[0_0_40px_rgba(0,0,0,0.6)]"
                            >
                                {/* Mobile Menu Header */}
                                <div className="flex items-center justify-between p-6 border-b border-gray-800/80">
                                    <div className="flex items-center gap-3">
                                        <img
                                            src="/assets/images/logo.png"
                                            alt="VSGrill Logo"
                                            className="h-10 w-auto object-contain"
                                        />
                                        <span className="text-white font-bold text-lg font-serif">VSGrill</span>
                                    </div>
                                    <button
                                        onClick={() => setMobileMenuOpen(false)}
                                        className="p-2 text-gray-400 hover:text-white transition-colors"
                                    >
                                        <FaTimes size={22} />
                                    </button>
                                </div>

                                {/* Mobile Menu Content */}
                                <div className="flex-1 px-6 py-8 overflow-y-auto">
                                    <nav className="space-y-2">
                                        {[
                                            { path: "/", label: t("nav.home") },
                                            { path: "/products", label: t("nav.products") },
                                            { path: "/gallery", label: t("nav.gallery") },
                                            { path: "/about", label: t("nav.about") },
                                        ].map((item) => (
                                            <Link
                                                key={item.path}
                                                to={item.path}
                                                onClick={() => setMobileMenuOpen(false)}
                                                className={`block w-full py-4 px-4 text-left text-lg font-medium rounded-xl border-l-4 transition-all ${location.pathname === item.path
                                                    ? "text-vsred-light bg-vsred/10 border-vsred"
                                                    : "text-gray-300 hover:text-vsred-light hover:bg-vsred/5 hover:border-vsred/60 border-transparent"
                                                    }`}
                                            >
                                                {item.label}
                                            </Link>
                                        ))}

                                        {/* CTA */}
                                        <div className="pt-6 border-t border-gray-800/70">
                                            <button
                                                className="w-full btn-primary mb-4"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setMobileMenuOpen(false);
                                                    const quoteSection = document.getElementById("quote");
                                                    if (quoteSection) {
                                                        quoteSection.scrollIntoView({ behavior: "smooth" });
                                                    } else {
                                                        navigate("/");
                                                        setTimeout(() => {
                                                            const quoteElement = document.getElementById("quote");
                                                            if (quoteElement) {
                                                                quoteElement.scrollIntoView({ behavior: "smooth" });
                                                            }
                                                        }, 100);
                                                    }
                                                }}
                                            >
                                                {t("nav.requestQuote")}
                                            </button>
                                        </div>

                                        {/* Language Switcher */}
                                        <div className="pt-6 border-t border-gray-800/70">
                                            <p className="text-gray-400 text-sm mb-3">
                                                {t("language.english")} / {t("language.polish")}
                                            </p>
                                            <LanguageSwitcher />
                                        </div>
                                    </nav>
                                </div>
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>
            </div>
        </motion.header>
    );
}
